import { useEffect, useState } from "react";
import { toolContentPath } from "../lib/apiPaths";

type ToolOutputResult = {
  output: unknown | undefined;
  loading: boolean;
};

const empty: ToolOutputResult = { output: undefined, loading: false };
const cache = new Map<string, unknown>();
const inFlight = new Map<string, Promise<unknown>>();

export function cacheToolOutput(entryId: string, output: unknown) {
  cache.set(entryId, output);
}

// Several rows can mount for one tool call at once; they share a single fetch.
function fetchToolOutput(entryId: string): Promise<unknown> {
  const pending = inFlight.get(entryId);
  if (pending) return pending;
  const request = fetch(toolContentPath(entryId), { credentials: "include" })
    .then(async (res) => {
      if (res.status === 204 || !res.ok) return undefined;
      const output = await res.json();
      cacheToolOutput(entryId, output);
      return output;
    })
    .finally(() => inFlight.delete(entryId));
  inFlight.set(entryId, request);
  return request;
}

export function useToolOutput(entryId: string, enabled: boolean) {
  const [loadedEntryId, setLoadedEntryId] = useState(entryId);
  const [result, setResult] = useState<ToolOutputResult>(() => {
    const cached = cache.get(entryId);
    return cached !== undefined ? { output: cached, loading: false } : empty;
  });
  if (loadedEntryId !== entryId) {
    setLoadedEntryId(entryId);
    const cached = cache.get(entryId);
    setResult(
      cached !== undefined ? { output: cached, loading: false } : empty
    );
  }

  useEffect(() => {
    if (!enabled) return;
    if (cache.has(entryId)) {
      // oxlint-disable-next-line react/set-state-in-effect -- EffectSetState: serves cached output, else resets at the top of the AbortController fetch
      setResult({ output: cache.get(entryId), loading: false });
      return;
    }

    let cancelled = false;
    setResult({ output: undefined, loading: true });

    void fetchToolOutput(entryId)
      .then((output) => {
        if (!cancelled) setResult({ output, loading: false });
      })
      .catch(() => {
        if (!cancelled) setResult(empty);
      });

    return () => {
      cancelled = true;
    };
  }, [entryId, enabled]);

  return result;
}
